import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  useDrawer
} from '@cat-ecom/pcc-components';
import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import { CatButton } from '@blocks-react/components/Button';
import { CatIconArrowBack } from '@blocks-react/components/Icons/ArrowBack';
import { CatInputField } from '@blocks-react/components/InputField';
import { CatIconChevronDown } from '@blocks-react/components/Icons/ChevronDown';
import { CatSearchForm } from '@blocks-react/components/SearchForm';
import { isEmpty, replaceTokensInString } from '@app/utils';
import styles from './MobileDropdownDrawer.module.scss';
import cx from 'classnames';

const MobileDropdownDrawer = ({
  label = '',
  drawerTitle = label,
  fieldId,
  dataTestId = 'mobile-dropdown-drawer',
  placeholder,
  items = [],
  required = false,
  requiredValidationMessage,
  additional,
  withSearch,
  searchPlaceHolder
}) => {
  const {
    drawerProps: defaultDrawerProps,
    openDrawer,
    isDrawerOpen,
    closeDrawer: onCloseDrawer
  } = useDrawer();
  const [filteredItems, setFilteredItems] = useState(items);
  const { t } = useTranslation();
  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext();

  const closeDrawer = () => {
    if (filteredItems.length !== items.length) {
      setFilteredItems(items);
    }
    onCloseDrawer();
  };

  const drawerProps = {
    ...defaultDrawerProps,
    onBlDrawerClose: e => {
      e.stopPropagation();
      closeDrawer(); //using custom closeDrawer to reset filteredItems
    }
  };

  const onItemSelected = value => {
    setValue(fieldId, value);
    additional && additional(value);
    closeDrawer();
  };

  const onSearchInputChange = e => {
    const searchText = e.target.value;
    const currentItems = items.filter(item =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredItems(currentItems);
  };

  const fieldNote = isEmpty(filteredItems)
    ? t('NO_RESULTS_TRY_ANOTHER_SEARCH')
    : '';

  return (
    <Controller
      name={fieldId}
      control={control}
      rules={{
        required: {
          value: required,
          message: replaceTokensInString(requiredValidationMessage, label)
        }
      }}
      render={({ field }) => (
        <>
          <CatInputField
            {...field}
            className={styles['mobile-dropdown-drawer__input-field']}
            name={fieldId}
            label={label}
            required={required}
            fieldId={fieldId}
            size="md"
            data-testid={dataTestId}
            value={field.value}
            readonly
            placeholder={placeholder}
            onClick={openDrawer}
            status={errors?.[fieldId] ? 'error' : undefined}
            fieldNote={
              errors?.[fieldId] ? errors?.[fieldId]?.message : undefined
            }
          >
            <CatIconChevronDown slot="after" onClick={openDrawer} />
          </CatInputField>

          {isDrawerOpen && (
            <Drawer {...drawerProps}>
              <DrawerHeader className={'d-flex align-items-center gap-3'}>
                <CatButton
                  className={cx(
                    'p-2',
                    styles['mobile-dropdown-drawer__arrow-btn']
                  )}
                  data-testid="back-button"
                  hideText
                  variant="ghost"
                  size="md"
                  onClick={closeDrawer}
                >
                  <CatIconArrowBack slot="before" />
                </CatButton>
                <h4
                  className={cx(
                    'cat-u-theme-typography-title m-0 text-capitalize',
                    styles['mobile-dropdown-drawer__title']
                  )}
                >
                  {drawerTitle}
                </h4>
              </DrawerHeader>
              <DrawerBody
                className={cx({
                  'pt-3 pb-5': !withSearch,
                  'd-flex flex-column gap-3': withSearch
                })}
              >
                {withSearch && (
                  <CatSearchForm
                    onBlChange={onSearchInputChange}
                    autocomplete="off"
                    size="md"
                    mobileBreakPoint={1}
                    placeholder={searchPlaceHolder}
                    fieldNote={fieldNote}
                    isEmpty
                  />
                )}
                <CatList>
                  {filteredItems.map(value => {
                    return (
                      <CatListItem
                        className={styles['mobile-dropdown-drawer__list-item']}
                        value={value}
                        key={value}
                        onClick={() => onItemSelected(value)}
                        isActive={value === field.value}
                      >
                        <div
                          className={
                            styles['mobile-dropdown-drawer__list-item-padding']
                          }
                        >
                          {value}
                        </div>
                      </CatListItem>
                    );
                  })}
                </CatList>
              </DrawerBody>
            </Drawer>
          )}
        </>
      )}
    />
  );
};

MobileDropdownDrawer.propTypes = {
  label: PropTypes.string,
  drawerTitle: PropTypes.string,
  fieldId: PropTypes.string,
  dataTestId: PropTypes.string,
  placeholder: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string),
  required: PropTypes.bool,
  requiredValidationMessage: PropTypes.string,
  additional: PropTypes.func,
  withSearch: PropTypes.bool,
  searchPlaceHolder: PropTypes.string
};

export default MobileDropdownDrawer;
